import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Crown } from 'lucide-react';
import { PersonalizaDownsellPopup } from './PersonalizaDownsellPopup';
import { BuyBar } from './personaliza/BuyBar';
import { OfertaEspecialPopup } from './personaliza/OfertaEspecialPopup';
import { useInView } from '../hooks/useInView';
import { trackAndRedirect } from '../lib/tracking';
import { MobileDownsellBanner } from './SlideInOffer';
export function PersonalizaSection() {
  const [showDownsell, setShowDownsell] = useState(false);
  const [showOfertaPopup, setShowOfertaPopup] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [vipRef, isVipInView] = useInView({
    threshold: 0.1
  });
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const handleAcceptDownsell = () => {
    window.location.href = 'https://betterdailyguides.com/cart/51978554671406:1';
  };
  const handleDeclineDownsell = () => {
    window.location.href = 'https://betterdailyguides.com/cart/51973733482798:1';
  };
  const handleBuyClick = () => {
    setShowOfertaPopup(true);
  };
  return <>
      <section ref={sectionRef} id="personaliza" className="py-10 sm:py-24 relative border-t border-white/5" data-id="element-491">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-492">
          <BuyBar onBuyClick={handleBuyClick} data-id="element-493" />
        </div>

        {/* VIP Block */}
        <div id="vip-section" ref={vipRef} className="max-w-4xl mx-auto px-4 sm:px-6 mt-12 sm:mt-16" data-id="element-494">
          <style dangerouslySetInnerHTML={{
          __html: `
                @keyframes sweepRightToLeftWhite {
                  0% { transform: translateX(200%); }
                  100% { transform: translateX(-200%); }
                }
              `
        }} data-id="element-495" />
          <motion.div variants={itemVariants} initial="hidden" animate={isVipInView ? 'visible' : 'hidden'} className="relative w-full min-h-[calc(100dvh-7rem)] sm:min-h-0 flex items-center justify-center bg-gradient-to-b from-[#111115] to-[#0A0A0B] border-2 border-[#d4af37]/30 rounded-2xl sm:rounded-[2rem] p-5 sm:p-14 shadow-[0_20px_80px_rgba(0,0,0,0.9),0_0_80px_rgba(255,255,255,0.12)] text-center overflow-hidden group" data-id="element-496">
            {/* Animated white border glow */}
            <div className="absolute inset-0 rounded-2xl sm:rounded-[2rem] pointer-events-none sm:animate-shimmer" style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            backgroundSize: '200% 100%'
          }} data-id="element-497" />

            {/* White Light Sweep Effect (Right to Left) */}
            <div className="absolute inset-0 pointer-events-none z-[2] opacity-40" style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)',
            width: '50%',
            animation: 'sweepRightToLeftWhite 4s ease-in-out infinite'
          }} data-id="element-498" />

            {/* Background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-lg h-full bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_70%)] pointer-events-none opacity-80" data-id="element-499" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-sm h-1/2 bg-[radial-gradient(ellipse_at_bottom,rgba(212,175,55,0.1),transparent_70%)] pointer-events-none" data-id="element-500" />

            <div className="relative z-10 w-full max-w-3xl mx-auto flex flex-col items-center justify-center" data-id="element-501">
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1 sm:py-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] text-[10px] sm:text-sm font-bold uppercase tracking-[2px] mb-4 sm:mb-8" data-id="element-502">
                <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-503" />
                Experiencia Completa
              </div>

              {/* Title */}
              <h3 className="font-serif text-3xl sm:text-5xl md:text-6xl font-black tracking-tight mb-2 sm:mb-4 text-white drop-shadow-[0_0_25px_rgba(255,255,255,0.15)]" data-id="element-504">
                Sala Maestra <span className="text-[#d4af37]" data-id="element-505">VIP</span>
              </h3>

              {/* Subtitle */}
              <p className="text-sm sm:text-xl md:text-2xl text-[#d4af37] font-bold mb-4 sm:mb-8" data-id="element-506">
                Acceso Completo a la Experiencia 888
              </p>

              {/* Description */}
              <div className="max-w-2xl mx-auto space-y-2 sm:space-y-4 text-xs sm:text-base md:text-lg text-white/80 leading-relaxed mb-6 sm:mb-10" data-id="element-507">
                <p data-id="element-508">
                  Un espacio privado con audios inmersivos diseñados para llevar
                  tu proceso a un nivel más profundo.
                </p>
                <p className="text-white font-semibold text-xs sm:text-base" data-id="element-509">
                  No reemplaza el sistema.{' '}
                  <span className="text-[#d4af37]" data-id="element-510">Lo amplifica.</span>
                </p>
              </div>

              {/* Price block */}
              <div className="w-full mb-5 sm:mb-8" data-id="element-511">
                <p className="text-[#d4af37] text-[9px] sm:text-sm uppercase tracking-[0.25em] mb-1 sm:mb-3 font-bold" data-id="element-512">
                  Acceso VIP + El Código 888 + 9 Archivos Maestros
                </p>
                <div className="relative inline-block" data-id="element-513">
                  <div className="absolute inset-0 -m-6 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.12),transparent_70%)] blur-2xl" style={{
                  animation: 'pulseGlow 3s ease-in-out infinite'
                }} data-id="element-514" />
                  <div className="text-5xl sm:text-6xl md:text-7xl font-black text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)] relative" data-id="element-515">
                    $26.39
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button onClick={e => {
              e.preventDefault();
              trackAndRedirect(26.39, 'Complete_Experience', 'https://www.betterdailyguides.com/cart/51978554671406:1');
            }} className="w-full sm:w-auto min-h-[52px] sm:min-h-[60px] px-6 sm:px-14 py-4 sm:py-5 rounded-full bg-[#d4af37] text-black font-black text-xs sm:text-base md:text-lg tracking-wider uppercase shadow-[0_15px_40px_rgba(212,175,55,0.4)] hover:shadow-[0_20px_50px_rgba(212,175,55,0.6)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 relative overflow-hidden group/btn" data-id="element-516">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-shimmer" data-id="element-517" />
                <span className="relative z-10" data-id="element-518">
                  ACTIVAR EXPERIENCIA COMPLETA
                </span>
              </button>

              <p className="mt-3 sm:mt-5 text-center text-[11px] sm:text-sm md:text-base leading-relaxed text-white/70 max-w-xl mx-auto" data-id="element-519">
                Al activar recibirás un código exclusivo para ingresar a{' '}
                <span className="text-[#d4af37] font-bold" data-id="element-520">
                  La Sala Maestra VIP
                </span>
                .
              </p>
            </div>
          </motion.div>

          <MobileDownsellBanner data-id="element-521" />
        </div>
      </section>

      <OfertaEspecialPopup isOpen={showOfertaPopup} onClose={() => setShowOfertaPopup(false)} data-id="element-522" />

      <PersonalizaDownsellPopup isOpen={showDownsell} onClose={() => setShowDownsell(false)} onAccept={handleAcceptDownsell} onDecline={handleDeclineDownsell} data-id="element-523" />
    </>;
}